import React from 'react';
import styles from './StanTooltip.module.css';
import { cn } from '@/lib/utils/cn';
import { useFloatingMenu } from '@/hooks/useFloatingTransition';
import { createIconElement } from '@/lib';
import InfoCircle from '@/assets/icons/ic-info-circle.svg';

interface StanTooltipProps {
    position?: 'top' | 'bottom' | 'left' | 'right';
    trigger?: 'hover' | 'click';
    type?: 'subtle' | 'contrast' | 'accent';
    children?: React.ReactNode;
    hideDelayInMs?: number;
    hideArrow?: boolean;
    className?: string;
    content?: React.ReactNode;
    showIcon?: boolean;
    iconPosition?: 'left' | 'right';
}

export const StanTooltip: React.FC<StanTooltipProps> = ({
    position = 'top',
    trigger = 'hover',
    type = 'subtle',
    children,
    hideDelayInMs = 0,
    hideArrow = false,
    className,
    content,
    showIcon = false,
    iconPosition = 'right',
}) => {
    const {
        isOpen: isVisible,
        refs,
        floatingStyles,
        context,
        getReferenceProps,
        getFloatingProps,
    } = useFloatingMenu({
        placement: position,
        trigger,
        hoverDelay: hideDelayInMs,
        offsetValue: 8,
    });

    const computedPosition = context.placement;

    const handleTooltipClick = (e: React.MouseEvent) => {
        // Prevent closing when clicking the tooltip content
        e.stopPropagation();
    };

    const icon = showIcon
        ? createIconElement(InfoCircle, {
              className: styles['tooltip-info-icon'],
          })
        : null;

    const renderContent = () => {
        if (!showIcon) {
            return children;
        }

        return (
            <span className={cn('inline-flex items-center gap-0.5', styles['tooltip-trigger'])}>
                {iconPosition === 'left' && icon}
                {children}
                {iconPosition === 'right' && icon}
            </span>
        );
    };

    return (
        <span {...getReferenceProps({ ref: refs.setReference })}>
            {renderContent()}
            <div
                {...getFloatingProps({
                    ref: refs.setFloating,
                    style: floatingStyles,
                    className: cn(
                        styles.tooltip,
                        styles[computedPosition],
                        styles[type],
                        isVisible ? styles.visible : styles.hidden,
                        hideArrow ? styles.noArrow : '',
                        className
                    ),
                    onClick: handleTooltipClick,
                })}
                role='tooltip'
                aria-hidden={!isVisible}
            >
                {content}
            </div>
        </span>
    );
};
